import { Display, FOV } from "rot-js"

import { Maze, MazePrefabs } from "../data/maze"
import { Color } from "../defines/colors"
import {
  Direction,
  EntryLocation,
  isPigment,
  ItemType,
  MapID,
} from "../defines/enums"
import { Symbol } from "../defines/symbols"
import {
  EnemySpec,
  EntityStack,
  MapBinder,
  MapItem,
  MazeRoomOptions,
  Offset,
  PaletteSpec,
  PopupSpec,
  PortalSpec,
  Position,
} from "../defines/types"
import {
  CollectibleProbability,
  CollectiblesDefaults,
  ItemDefaults,
  PIGMENT_DROP,
} from "../defines/values"
import { BounceAsyncEnemy } from "../entity/enemies/bounce/async"
import { BounceSyncEnemy } from "../entity/enemies/bounce/sync"
import { Enemy } from "../entity/enemies/enemy"
import { PathAsyncEnemy } from "../entity/enemies/path/async"
import { PathSyncEnemy } from "../entity/enemies/path/sync"
import { Entity } from "../entity/entity"
import { Elixir } from "../entity/items/elixir"
import { Item } from "../entity/items/item"
import { Pigment } from "../entity/items/pigment"
import { PigmentDrop } from "../entity/items/pigmentdrop"
import { MapPortal } from "../entity/other/mapportal"
import { PopupTrigger } from "../entity/other/popuptrigger"
import { Residue } from "../entity/other/residue"
import { Player } from "../player/player"
import { getCollectibleRender, isCollectible } from "../utils/collectibles"
import { makeEnemy, makeTrap } from "../utils/enemy"
import {
  getRandomPigment,
  makeBinder,
  makeItem,
  shouldPlaceRandomly,
} from "../utils/item"
import { mapIDToString } from "../utils/map"
import { isSamePosition, shuffle } from "../utils/misc"
import {
  generateRandomNumber,
  generateRandomNumberZeroToOne,
} from "../utils/rng"
import { Room } from "./room"
import { DANGER_TILE, Tile, WALL_TILE } from "./tile"

export class TapestryMap {
  protected _entities: EntityStack[]
  protected _tiles: Tile[][]

  private _dropNearX = -3
  private _dropNearXStart = -3
  private _dropNearXStop = 2
  private _dropNearY = -2
  private _dropNearYStart = -2
  private _dropNearYStop = 2

  protected constructor(
    public mapID: MapID,
    public display: Display,
    public player: Player,
    public width: number,
    public height: number,
    public viewportWidth: number,
    public viewportHeight: number,
    public enableFogOfWar: boolean
  ) {
    this._entities = []

    this._tiles = new Array(this.height)
    for (let y = 0; y < this.height; y++) {
      const row = new Array(this.width)
      for (let x = 0; x < this.width; x++) {
        row[x] = { ...WALL_TILE }
      }
      this._tiles[y] = row
    }
  }

  destroy(): void {
    this._entities.forEach((stack) => {
      stack.stack.forEach((e) => {
        if (e instanceof BounceAsyncEnemy || e instanceof PathAsyncEnemy) {
          e.stopTimer()
        }
      })
    })
  }

  get toString(): string {
    return mapIDToString(this.mapID)
  }

  placeBinder(binder: MapBinder, max: number, rooms: Room[]): void {
    const amount = generateRandomNumber(binder.minMap, binder.maxMap)
    let placed = 0
    let placedIdx = []
    while (placed < amount && placed < max) {
      let idx = generateRandomNumber(1, rooms.length - 1)
      while (idx in placedIdx) {
        idx = generateRandomNumber(1, rooms.length - 1)
      }
      placedIdx.push(idx)

      const bounds = rooms[idx].bounds
      while (true) {
        const x = generateRandomNumber(bounds.x1 + 1, bounds.x2 - 1)
        const y = generateRandomNumber(bounds.y1 + 1, bounds.y2 - 1)
        if (this.canPlace({ x: x, y: y })) {
          const position = { x: x, y: y }
          this.addEntity(makeBinder(position))
          placed += 1
          break
        }
      }
    }
  }

  protected shouldPlaceCollectible(): ItemType {
    // common
    if (!this.player.hasCommonCollectible(this.mapID)) {
      const prob = generateRandomNumberZeroToOne()
      if (prob < CollectibleProbability.COMMON) {
        return ItemType.COLLECTIBLE_COMMON
      }
    }

    // mythic
    if (!this.player.hasMythicCollectible(this.mapID)) {
      const prob = generateRandomNumberZeroToOne()
      if (prob < CollectibleProbability.MYTHIC) {
        return ItemType.COLLECTIBLE_MYTHIC
      }
    }

    return ItemType.NONE
  }

  protected placeCollectible(type: ItemType, position: Position): void {
    const render = getCollectibleRender(type)
    if (render === undefined) {
      return
    }

    let name = type == ItemType.COLLECTIBLE_COMMON ? "common" : "mythic"
    name += " collectible"
    const item = new Item(
      type,
      CollectiblesDefaults.MIN_REQ_LEVEL,
      position,
      render,
      name
    )
    this.addEntity(item)
  }

  placeEnemies(enemies: EnemySpec[], rooms: Room[]): void {
    enemies.forEach((e) => {
      let placed = 0
      let placedIdx: number[] = []

      let idx = Array.from(rooms.keys())
      shuffle(idx)

      for (let i = 0; i < idx.length; ++i) {
        placed += this.placeEnemy(rooms[idx[i]], e)
        if (placed >= e.mapMax) {
          break
        }
      }
    })
  }

  placeEquipment(equip: MapItem[], rooms: Room[]): void {
    this.placeItems(equip, rooms)
  }

  placeItems(items: MapItem[], rooms: Room[]): void {
    if (items !== undefined && items.length > 0) {
      // drops are placed in a "box" around the player
      // 0: LR across the top of the box
      // 1: OR across the bottom of the box
      // 2: TB left side of box
      // 3: TB right side of box
      let dropPlacement = 0

      for (let x = 0; x < items.length; ++x) {
        const i = items[x]
        if (this.player.hasMapItem(i)) {
          continue
        }

        let times = 1
        if (i.minMax !== undefined) {
          times = generateRandomNumber(i.minMax.min, i.minMax.max)
        }

        let position = undefined
        let item = undefined

        for (let t = 0; t < times; ++t) {
          if (shouldPlaceRandomly(i)) {
            const idx = generateRandomNumber(0, rooms.length - 1)
            position = this.placeItem(rooms[idx])
          } else {
            if (i.dropNearPlayer !== undefined) {
              position = {
                x: this.player.position.x + this._dropNearX,
                y: this.player.position.y + this._dropNearY,
              }
              if (dropPlacement == 0) {
                // top of box
                this._dropNearX += 1
                if (this._dropNearX == this._dropNearXStop) {
                  this._dropNearX = this._dropNearXStart
                  this._dropNearY = this._dropNearYStop
                  dropPlacement += 1
                }
              } else if (dropPlacement == 1) {
                // bottom of box
                this._dropNearX += 1
                if (this._dropNearX == this._dropNearXStop) {
                  this._dropNearX = this._dropNearXStart
                  this._dropNearY = this._dropNearYStart + 1
                  dropPlacement += 1
                }
              } else if (dropPlacement == 2) {
                // left side of box
                this._dropNearY += 1
                if (this._dropNearY == this._dropNearYStop - 1) {
                  this._dropNearX = this._dropNearXStop
                  this._dropNearY = this._dropNearYStart + 1
                  dropPlacement += 1
                }
              } else if (dropPlacement == 3) {
                // right side of box
                this._dropNearY += 1
                if (this._dropNearY == this._dropNearYStop - 1) {
                  // unlikely, but this will restart at top of box
                  this._dropNearX = this._dropNearXStart
                  this._dropNearY = this._dropNearYStart
                  dropPlacement = 0
                }
              }
            } else {
              if (i.column !== undefined && i.row !== undefined) {
                position = { x: i.column, y: i.row }
              }
            }
          }
          if (position !== undefined) {
            item = makeItem(i, position)
            if (item !== undefined) {
              this.addEntity(item)
            }
          }
        }
      }
    }
  }

  placePigments(palette: PaletteSpec, rooms: Room[]): void {
    Object.entries(palette).forEach(([color, config]) => {
      let placed = 0
      let placedIdx: number[] = []

      let idx = Array.from(rooms.keys())
      shuffle(idx)

      for (let i = 0; i < idx.length; ++i) {
        placed += this.placePigment(
          rooms[idx[i]],
          color,
          config.range.min,
          config.range.max
        )

        if (placed >= config.mapMax) {
          break
        }
      }
    })
  }

  placePopups(popups: PopupSpec[], rooms: Room[]): void {
    popups.forEach((p) => {
      this.addEntity(new PopupTrigger(p.target, { x: p.column, y: p.row }))
    })
  }

  placePortals(portals: PortalSpec[], rooms: Room[]): Position {
    if (portals.length == 0) {
      throw new Error("Map must have a portal to somewhere")
    }

    portals.forEach((portal) => {
      this.placePortal(rooms[portal.room].center, portal.target)
    })

    const startPoint = rooms[portals[0].room].center
    return startPoint
  }

  addMazeRooms(mazeRooms: MazeRoomOptions[], rooms: Room[]): number[] {
    let mazeRoomIndexes: number[] = []
    mazeRooms.forEach((room) => {
      const mazeRoomIndex = this.addMazeRoom(room, rooms)
      mazeRoomIndexes.push(mazeRoomIndex)
    })

    return mazeRoomIndexes
  }

  addRoom(x: number, y: number, roomTiles: Tile[][]) {
    for (let curY = y; curY < y + roomTiles.length; curY++) {
      const mapRow = this._tiles[curY]
      const roomRow = roomTiles[curY - y]
      for (let curX = x; curX < x + roomRow.length; curX++) {
        mapRow[curX] = roomRow[curX - x]
        if (!this.enableFogOfWar) {
          mapRow[curX].visible = true
        }
      }
    }
  }

  addMazeRoom(roomOptions: MazeRoomOptions, rooms: Room[]): number {
    let prefabIndex = generateRandomNumber(0, MazePrefabs.length - 1)
    if (roomOptions.prefabSelection !== undefined) {
      prefabIndex = roomOptions.prefabSelection
    }

    const prefab = MazePrefabs[prefabIndex]

    // avoid rooms[0] since this will have the portal
    const mazeRoomIndex = generateRandomNumber(1, rooms.length - 1)
    const room = rooms[mazeRoomIndex]

    for (let y = 0; y < prefab.length; ++y) {
      for (let x = 0; x < prefab[y].length; ++x) {
        if (prefab[y][x] == Symbol.DEVOURER) {
          const trap = makeTrap(
            {
              x: room.x + x + Maze.BORDER,
              y: room.y + y + Maze.BORDER,
            },
            roomOptions.colors
          )
          if (trap !== undefined) {
            this.addEntity(trap)
          }
        } else if (prefab[y][x] == Symbol.ITEM) {
          const position = {
            x: room.x + x + Maze.BORDER,
            y: room.y + y + Maze.BORDER,
          }
          let item = this.shouldPlaceCollectible()

          if (item == ItemType.NONE) {
            const entity = buildItem(
              position.x,
              position.y,
              ItemType.PIGMENT_RANDOM,
              PIGMENT_DROP
            )
            if (entity) {
              this.addEntity(entity)
            }
          } else {
            this.placeCollectible(item, position)
          }
        }

        this._tiles[room.y + y + Maze.BORDER][room.x + x + Maze.BORDER] = {
          ...DANGER_TILE,
        }
      }
    }

    return mazeRoomIndex
  }

  canWalk(x: number, y: number): boolean {
    return this._tiles[y][x].walkable
  }

  private hasBlockingEntity(stack: Entity[]): boolean {
    if (stack.length == 0) {
      return false
    } else {
      return stack[0].blocks
    }
  }

  // Add an Entity to the stack at e.y, e.x.
  //   A stack can only have a single blocking Entity,
  //   and that Entity will be at the "top" [0] of the stack.
  addEntity(e: Entity): boolean {
    for (let i = 0; i < this._entities.length; ++i) {
      const group = this._entities[i]
      if (
        group.position.x == e.position.x &&
        group.position.y == e.position.y
      ) {
        if (e.blocks) {
          if (this.hasBlockingEntity(group.stack)) {
            return false
          } else {
            // put blocking entity on "top"
            group.stack.unshift(e)
            return true
          }
        } else {
          // this entity can go on the "bottom"
          group.stack.push(e)
          return true
        }
      }
    }

    // new stack
    this._entities.push({ position: e.position, stack: [e] })
    return true
  }

  findEntities(position: Position): Entity[] {
    let entities: Entity[] = []

    for (let i = 0; i < this._entities.length; ++i) {
      const group = this._entities[i]
      if (group.stack && isSamePosition(position, group.position)) {
        group.stack.forEach((s) => {
          entities.push(s)
        })
      }
    }

    return entities
  }

  findInteractables(position: Position, direction: Direction): Entity[] {
    let targetX = position.x
    let targetY = position.y
    switch (direction) {
      case Direction.UP:
        targetY = position.y - 1
        break
      case Direction.DOWN:
        targetY = position.y + 1
        break
      case Direction.RIGHT:
        targetX = position.x + 1
        break
      case Direction.LEFT:
        targetX = position.x - 1
        break
      default:
        break
    }

    return this.findEntities({ x: targetX, y: targetY })
  }

  placeResidues(): void {
    // place the corpses on top, so corpses render over top
    //   i.e., other items are underneath and not visible
    for (let i = 0; i < this._entities.length; ++i) {
      const group = this._entities[i]
      if (group.stack) {
        for (let j = 0; j < group.stack.length; ++j) {
          const e = group.stack[j]
          if (e.baseClass == "Enemy") {
            const enemy = e as Enemy
            if (!enemy.isAlive()) {
              group.stack.splice(j, 1)
              group.stack.unshift(
                new Residue(enemy.position, enemy.displayName)
              )
              return
            }
          }
        }
      }
    }
  }

  removeTopmostEntity(position: Position): void {
    for (let i = 0; i < this._entities.length; ++i) {
      const group = this._entities[i]
      if (isSamePosition(position, group.position) && group.stack) {
        group.stack.splice(0, 1)
        return
      }
    }
  }

  removeItem(position: Position, type: ItemType): void {
    for (let i = 0; i < this._entities.length; ++i) {
      const group = this._entities[i]
      if (isSamePosition(position, group.position) && group.stack) {
        for (let j = 0; j < group.stack.length; ++j) {
          const e = group.stack[j] as Item
          if (e.type == type) {
            group.stack.splice(j, 1)
            return
          }
        }
      }
    }
  }

  removeEntities(position: Position): void {
    for (let i = 0; i < this._entities.length; ++i) {
      const group = this._entities[i]
      if (isSamePosition(position, group.position)) {
        this._entities.splice(i, 1)
        return
      }
    }
  }

  inBounds(x: number, y: number): boolean {
    return (
      x >= 0 &&
      x < this.width &&
      y >= 0 &&
      y < this.height &&
      this.canWalk(x, y)
    )
  }

  isVisible(x: number, y: number): boolean {
    if (this._tiles[y] === undefined || !this._tiles[y][x] === undefined) {
      return false
    }

    return this._tiles[y][x].visible
  }

  lightPasses(x: number, y: number): boolean {
    if (this.inBounds(x, y)) {
      return this._tiles[y][x].transparent
    }
    return false
  }

  public get pathSyncEnemies(): PathSyncEnemy[] {
    let chasers: PathSyncEnemy[] = []

    this._entities.forEach((group) => {
      if (group.stack) {
        const moreChasers = group.stack
          .filter((e) => e instanceof PathSyncEnemy)
          .map((e) => e as PathSyncEnemy)
          .filter((e) => e.isAlive())
        chasers = chasers.concat(moreChasers)
      }
    })

    return chasers
  }

  public get bounceSyncEnemies(): BounceSyncEnemy[] {
    let chasers: BounceSyncEnemy[] = []

    this._entities.forEach((group) => {
      if (group.stack) {
        const moreChasers = group.stack
          .filter((e) => e instanceof BounceSyncEnemy)
          .map((e) => e as BounceSyncEnemy)
          .filter((e) => e.isAlive())
        chasers = chasers.concat(moreChasers)
      }
    })

    return chasers
  }

  private canPlace(position: Position): boolean {
    return (
      !this._entities.some((e) => isSamePosition(e.position, position)) &&
      !isSamePosition(position, this.player.position)
    )
  }

  private placePigment(
    room: Room,
    color: string,
    min: number,
    max: number
  ): number {
    const bounds = room.bounds

    const toPlace: number = generateRandomNumber(min, max)
    let placed: number = 0
    while (placed < toPlace) {
      const x = generateRandomNumber(bounds.x1 + 1, bounds.x2 - 1)
      const y = generateRandomNumber(bounds.y1 + 1, bounds.y2 - 1)
      if (this.canPlace({ x: x, y: y })) {
        const position = { x: x, y: y }
        this.addEntity(new Pigment(color, position))
        placed += 1
      }
    }

    return placed
  }

  private placeItem(room: Room): Position | undefined {
    const bounds = room.bounds

    let tries = 0
    while (tries < ItemDefaults.MAX_RANDOM_ITEM_PLACEMENT_TRIES) {
      const x = generateRandomNumber(bounds.x1 + 1, bounds.x2 - 1)
      const y = generateRandomNumber(bounds.y1 + 1, bounds.y2 - 1)
      if (this.canPlace({ x: x, y: y })) {
        return { x: x, y: y }
      }
      tries += 1
    }

    return undefined
  }

  private placeEnemy(room: Room, enemy: EnemySpec): number {
    const bounds = room.bounds

    const toPlace: number = generateRandomNumber(enemy.min, enemy.max)
    let placed: number = 0
    while (placed < toPlace) {
      const x = generateRandomNumber(bounds.x1 + 1, bounds.x2 - 1)
      const y = generateRandomNumber(bounds.y1 + 1, bounds.y2 - 1)
      if (this.canPlace({ x: x, y: y })) {
        const position = { x: x, y: y }
        const nextEnemy = makeEnemy(position, enemy, this.player)
        if (nextEnemy) {
          this.addEntity(nextEnemy)
          window.engine.addBeastToBestiary(enemy.type)
          placed += 1
        }
      }
    }

    return placed
  }

  private placePortal(position: Position, mapID: MapID): void {
    this.addEntity(new MapPortal(mapID, position))
  }

  protected getCameraOffsets(position: Position): Offset {
    let half: number = this.viewportWidth / 2
    let cameraX: number = position.x - half
    if (position.x < half) {
      cameraX = 0
    } else if (position.x > this.width - half) {
      cameraX = this.width - this.viewportWidth
    }

    half = this.viewportHeight / 2
    let cameraY: number = position.y - half
    if (position.y < half) {
      cameraY = 0
    } else if (position.y >= this.height - half) {
      cameraY = this.height - this.viewportHeight
    }

    return { xOffset: cameraX, yOffset: cameraY }
  }

  clearTile(x: number, y: number, display: Display, player: Player): void {
    const camera = this.getCameraOffsets(player.position)
    const row = this._tiles[y]
    const tile = row[x]
    if (tile.visible) {
      display.draw(
        camera.xOffset + x,
        camera.yOffset + y,
        ".",
        tile.light.fg,
        tile.light.bg
      )
    } else {
      display.draw(
        camera.xOffset + x,
        camera.yOffset + y,
        ".",
        tile.dark.fg,
        tile.dark.bg
      )
    }
  }

  getCollectibleCount(): number {
    let count = 0
    this._entities.forEach((stack) => {
      stack.stack.forEach((e) => {
        if (e instanceof Item && isCollectible(e.type)) {
          count += 1
        }
      })
    })
    return count
  }

  getEnemyCount(): number {
    let count = 0
    this._entities.forEach((stack) => {
      stack.stack.forEach((e) => {
        if (e.baseClass == "Enemy") {
          count += 1
        }
      })
    })
    return count
  }

  getItemCount(): number {
    let count = 0
    this._entities.forEach((stack) => {
      stack.stack.forEach((e) => {
        if (e instanceof Item && !isPigment(e.type) && !isCollectible(e.type)) {
          count += 1
        }
      })
    })
    return count
  }

  getPigmentCount(): number {
    let count = 0
    this._entities.forEach((stack) => {
      stack.stack.forEach((e) => {
        if (e instanceof Item && isPigment(e.type)) {
          count += 1
        }
      })
    })
    return count
  }

  render(): Offset {
    const camera = this.getCameraOffsets(this.player.position)

    for (let y = 0; y < this.viewportHeight; y++) {
      const row = this._tiles[camera.yOffset + y]
      for (let x = 0; x < this.viewportWidth; x++) {
        const tile = row[camera.xOffset + x]
        if (window.debug) {
          this.debugDraw({ x: x, y: y }, tile)
        } else {
          this.fovDraw({ x: x, y: y }, tile)
        }
      }
    }

    this._entities.forEach((group) => {
      if (group.stack.length > 0) {
        const entity = group.stack[0]
        this.renderEntity(entity, camera)
      }
    })

    return camera
  }

  private getEnemyAtPosition(pos: Position): Enemy | undefined {
    for (let i = 0; i < this._entities.length; ++i) {
      const group = this._entities[i]
      for (let j = 0; j < group.stack.length; ++j) {
        const entity = group.stack[j]
        if (entity.position.x == pos.x && entity.position.y == pos.y)
          return entity as Enemy
      }
    }

    return undefined
  }

  renderEntity(e: Entity, cameraOffset?: Offset): void {
    let camera = cameraOffset
    if (camera === undefined) {
      camera = this.getCameraOffsets(this.player.position)
    }

    if (window.debug) {
      const render = e.renderLight()
      this.display.drawOver(
        e.position.x - camera.xOffset,
        e.position.y - camera.yOffset,
        render.symbol,
        render.color,
        null
      )
    } else {
      if (e.optic !== undefined) {
        this.renderOpticEntity(e, camera)
      } else {
        this.renderNonOpticEntity(e, camera)
      }
    }
  }

  updateFov() {
    if (this.enableFogOfWar) {
      for (let y = 0; y < this.height; y++) {
        for (let x = 0; x < this.width; x++) {
          this._tiles[y][x].visible = false
        }
      }

      const fov = new FOV.PreciseShadowcasting(this.lightPasses.bind(this))
      fov.compute(
        this.player.position.x,
        this.player.position.y,
        this.player.vision,
        (x, y, _r, visibility) => {
          if (visibility === 1) {
            this._tiles[y][x].visible = true
            this._tiles[y][x].seen = true
            const enemy = this.getEnemyAtPosition({ x: x, y: y }) as Enemy
            if (enemy !== undefined) {
              window.engine.seeBeast(enemy.type)
            }
          }
        }
      )
    }
  }

  private renderOpticEntity(e: Entity, offset: Offset): void {
    if (e.optic !== undefined && this.player.hasVision(e.optic)) {
      let render = undefined

      if (this._tiles[e.position.y][e.position.x].visible) {
        render = e.renderLight()
      } else if (this._tiles[e.position.y][e.position.x].seen) {
        render = e.renderDark()
      }

      if (render !== undefined) {
        this.display.drawOver(
          e.position.x - offset.xOffset,
          e.position.y - offset.yOffset,
          render.symbol,
          render.color,
          null
        )
      }
    }
  }

  private renderNonOpticEntity(e: Entity, offset: Offset): void {
    let render = undefined
    if (this._tiles[e.position.y][e.position.x].visible) {
      render = e.renderLight()
    } else if (this._tiles[e.position.y][e.position.x].seen) {
      render = e.renderDark()
    }

    if (render !== undefined) {
      this.display.drawOver(
        e.position.x - offset.xOffset,
        e.position.y - offset.yOffset,
        render.symbol,
        render.color,
        null
      )
    }
  }

  private debugDraw(position: Position, tile: Tile): void {
    this.display.draw(
      position.x,
      position.y,
      tile.light.symbol,
      tile.light.fg,
      tile.light.bg
    )
  }

  private fovDraw(position: Position, tile: Tile): void {
    let symbol = Symbol.BLANK
    let fg = Color.BLACK
    let bg = Color.BLACK

    if (tile.visible) {
      symbol = tile.light.symbol
      fg = tile.light.fg
      bg = tile.light.bg
    } else if (tile.seen) {
      symbol = tile.dark.symbol
      fg = tile.dark.fg
      bg = tile.dark.bg
    }

    this.display.draw(position.x, position.y, symbol, fg, bg)
  }
}

function getDoorPosition(room: Room, loc: EntryLocation): Position {
  let position = room.topCenter

  switch (loc) {
    case EntryLocation.RIGHT_CENTER:
      position = room.rightCenter
      break
    case EntryLocation.BOTTOM_CENTER:
      position = room.bottomCenter
      break
    case EntryLocation.LEFT_CENTER:
      position = room.leftCenter
      break
    default:
      break
  }

  return position
}

function buildItem(
  x: number,
  y: number,
  type: ItemType,
  amount?: number
): Entity | undefined {
  const position = { x: x, y: y }

  let item = undefined
  switch (type) {
    case ItemType.ELIXIR:
      item = new Elixir(position)
      break
    case ItemType.PIGMENT_RANDOM:
      item = new PigmentDrop(getRandomPigment(), position, amount)
      break
    default:
      break
  }

  return item
}

export { getDoorPosition }
